/**
 * Calculate weapon tonnage based on Traveller rules and exceptions
 * 
 * General Rule: Weapons don't consume volume (except specific exceptions)
 * 
 * Exceptions:
 * - Railgun turret: 1 ton for ammunition (per canister system)
 * - Railgun fixed mount: 1 ton for ammunition (per canister system)
 * - Railgun barbette: 2 tons for ammunition (1 canister with 12 attacks, per canister system)
 * - Missile barbettes on Firmpoints: +2 tons
 * - Torpedo barbettes on Firmpoints: +2 tons
 * - Railgun bays: per attack system (not implemented yet)
 * - Railgun spinal: 5 rounds (20 tons each = 100 tons total, per attack system)
 * - Mass Driver spinal: 50 tons ammo per attack (per attack system)
 */

export interface WeaponTonnageContext {
  weaponName: string;
  mountType: string;
  shipSize: number;
  isSpinalMount?: boolean;
  spinalSizeMultiple?: number;
}

/**
 * Calculate tonnage for a single weapon based on Traveller rules
 */
export const calculateWeaponTonnage = (context: WeaponTonnageContext): number => {
  const { weaponName, mountType, shipSize, isSpinalMount, spinalSizeMultiple } = context;
  
  // Spinal mounts have their own rules
  if (isSpinalMount) {
    return calculateSpinalMountTonnage(weaponName, spinalSizeMultiple || 1);
  }
  
  // Standard weapon tonnage rules
  switch (weaponName.toLowerCase()) {
    case 'railgun':
      if (mountType === 'barbette') {
        // Railgun barbettes contain enough ammunition for 12 attacks
        // Each canister of railgun ammunition consumes two tons and costs Cr10000
        return 2; // 1 canister (2 tons) contains 12 attacks worth of ammo (per canister system)
      } else if (['fixed', 'turret'].includes(mountType)) {
        // Fixed mount and turret railguns: 1 ton for ammunition (per canister system)
        return 1;
      } else if (['smallBay', 'mediumBay', 'largeBay'].includes(mountType)) {
        // Railgun bays use per attack system (not implemented yet)
        return 0;
      } else {
        // Other mount types
        return 0;
      }
    
    case 'missile':
    case 'torpedo':
      // Missile/Torpedo barbettes on Firmpoints consume an additional two tons of space
      if (mountType === 'barbette' && shipSize < 100) {
        return 2;
      }
      return 0; // No tonnage otherwise
    
    default:
      // All other weapons: no tonnage consumption
      return 0;
  }
};

/**
 * Calculate spinal mount weapon tonnage
 */
export const calculateSpinalMountTonnage = (weaponName: string, sizeMultiple: number): number => {
  switch (weaponName.toLowerCase()) {
    case 'mass driver':
      // Mass Driver spinal mount: 50 tons ammo per attack
      return 50;
    
    case 'railgun':
      // Railgun spinal mounts come equipped with five rounds
      // Extra or replacement rounds consume 20 tons each
      return 5 * 20; // 100 tons for 5 rounds
    
    case 'meson':
    case 'particle':
      // Meson and Particle spinal mounts don't have separate ammo storage
      return 0;
    
    default:
      return 0;
  }
};

/**
 * Calculate total tonnage for all weapons in a mount
 */
export const calculateMountWeaponTonnage = (
  weapons: Array<{ name: string }>,
  mountType: string,
  shipSize: number
): number => {
  return weapons.reduce((total, weapon) => {
    const context: WeaponTonnageContext = {
      weaponName: weapon.name,
      mountType,
      shipSize,
    };
    return total + calculateWeaponTonnage(context);
  }, 0);
};

/**
 * Check if a weapon has special tonnage requirements
 */
export const hasSpecialTonnage = (weaponName: string, mountType: string, shipSize: number): boolean => {
  return calculateWeaponTonnage({
    weaponName,
    mountType,
    shipSize,
  }) > 0;
};

/**
 * Get tonnage description for a weapon
 */
export const getWeaponTonnageDescription = (
  weaponName: string,
  mountType: string,
  shipSize: number
): string => {
  const tonnage = calculateWeaponTonnage({
    weaponName,
    mountType,
    shipSize,
  });
  
  if (tonnage === 0) return 'No tonnage consumption';
  
  switch (weaponName.toLowerCase()) {
    case 'railgun':
      if (mountType === 'barbette') {
        return `${tonnage} tons (1 canister with 12 attacks worth of ammunition)`;
      } else if (mountType === 'fixed' || mountType === 'turret') {
        return `${tonnage} ton (ammunition storage per canister)`;
      } else {
        return `${tonnage} ton (ammunition storage)`;
      }
    
    case 'missile':
    case 'torpedo':
      return `${tonnage} tons (Firmpoint additional space)`;
    
    default:
      return `${tonnage} tons`;
  }
};

/**
 * Check if a weapon uses "per canister" or "per attack" ammunition system
 */
export const getAmmunitionSystem = (weaponName: string, mountType: string): 'per-canister' | 'per-attack' | 'none' => {
  switch (weaponName.toLowerCase()) {
    case 'railgun':
      if (['fixed', 'turret', 'barbette'].includes(mountType)) {
        return 'per-canister';
      } else if (['smallBay', 'mediumBay', 'largeBay', 'spinalMount'].includes(mountType)) {
        return 'per-attack';
      }
      return 'none';
    
    case 'mass driver':
      if (mountType === 'spinalMount') {
        return 'per-attack';
      }
      return 'none';
    
    default:
      return 'none';
  }
};
